import { useState } from 'react';
import { adminApi } from '../../api/admin';
import { useAsync, Banner, Item, Empty } from './ui';
import { Modal } from '../../components/Modal';

type Row = Record<string, unknown>;
const blank = { operatorId: '', name: '', role: 'operator', active: true, pin: '' };
const isYes = (v: unknown) => v === true || String(v).toLowerCase() === 'true';

export default function OperatorsAdmin() {
  const { data, error, loading, reload } = useAsync(() => adminApi.listOperators(), [], 'operators');
  const [form, setForm] = useState<typeof blank | null>(null);
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);

  const isExisting = !!form && !!data?.rows.find((r) => String(r['Operator ID']) === form.operatorId);

  async function save() {
    if (!form) return;
    setSaving(true); setMsg('');
    try {
      const payload: Record<string, unknown> = { ...form };
      if (!payload.pin) delete payload.pin;
      await adminApi.saveOperator(payload);
      setForm(null); reload();
    } catch (e) { setMsg(e instanceof Error ? e.message : 'Save failed'); }
    finally { setSaving(false); }
  }

  return (
    <div>
      <div className="spread">
        <h1>Operators</h1>
        <button className="sm" onClick={() => { setForm({ ...blank }); setMsg(''); }}>+ Add</button>
      </div>
      <Banner error={error || msg} />
      {loading && <div className="spinner" />}
      {data && data.rows.length === 0 && <Empty icon="👷">No operators yet.</Empty>}

      <div className="cardlist">
        {data?.rows.map((r: Row, i) => (
          <Item
            key={i}
            stripe={isYes(r['Active']) ? 'ok' : 'bad'}
            title={<>{String(r['Name'])} {r['Role'] === 'admin' && <span className="chip" style={{ fontSize: '.7rem' }}>admin</span>}</>}
            meta={<>
              <span>{String(r['Operator ID'])}</span>
              <span>{r['Has PIN'] ? 'PIN set' : 'no PIN'}</span>
              {!isYes(r['Active']) && <span style={{ color: 'var(--bad)' }}>inactive</span>}
            </>}
            actions={<button className="secondary xs" onClick={() => setForm({
              operatorId: String(r['Operator ID'] ?? ''), name: String(r['Name'] ?? ''),
              role: String(r['Role'] ?? 'operator'), active: isYes(r['Active']), pin: '',
            })}>Edit</button>}
          />
        ))}
      </div>

      {form && (
        <Modal
          title={isExisting ? 'Edit operator' : 'New operator'}
          onClose={() => !saving && setForm(null)}
          footer={<>
            <button className="secondary" onClick={() => setForm(null)} disabled={saving}>Cancel</button>
            <button onClick={save} disabled={saving || !form.operatorId || !form.name}>{saving ? 'Saving…' : 'Save'}</button>
          </>}
        >
          <label>Employee ID</label>
          <input value={form.operatorId} disabled={isExisting}
            onChange={(e) => setForm({ ...form, operatorId: e.target.value.toUpperCase() })} placeholder="E002" />
          <label>Name</label>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <label>Role</label>
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
            <option value="operator">Operator</option>
            <option value="admin">Admin</option>
          </select>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 14 }}>
            <input type="checkbox" style={{ width: 'auto', minHeight: 0 }} checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active
          </label>
          <label>{isExisting ? 'Reset PIN' : 'PIN'} {form.role === 'admin' ? '(6–8 digits)' : '(4–8 digits)'}</label>
          <input inputMode="numeric" value={form.pin}
            onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, '') })}
            placeholder={isExisting ? 'leave blank to keep current' : ''} />
        </Modal>
      )}
    </div>
  );
}
